import type { ChaveDeCadastro, ResumoDosCadastros } from './cadastros.contrato';
import { totalAtivoDoDominio, totalDoDominio, type ChaveDeDominio } from './dados';

/**
 * Domínio que alimenta um contador: ou um cadastro que já lê a API (chave de
 * endereço, em kebab), ou um que ainda lê o dado temporário (`dados.ts`).
 */
export type ChaveDeContagem = ChaveDeDominio | ChaveDeCadastro;

const CADASTROS_DA_API: ReadonlySet<string> = new Set<ChaveDeCadastro>([
  'marcas-veiculo',
  'modelos-veiculo',
  'versoes-veiculo',
  'tipos-veiculo',
  'cores-veiculo',
  'tipos-unidade-escolar',
  'categorias-cnh',
  'tipos-vinculo',
  'cursos-obrigatorios',
  'tipos-ocorrencia',
  'motivos-cancelamento-os',
  'motivos-substituicao',
  'tipos-publicacao',
  'seguradoras',
  'escolas',
]);

export function ehCadastroDaApi(chave: string): chave is ChaveDeCadastro {
  return CADASTROS_DA_API.has(chave);
}

/**
 * Total e ativos de um domínio. Cadastro da API vem do resumo (que pode ainda não
 * ter chegado: devolve `null`); o resto sai do dado temporário, na hora.
 */
export function contagemDoDominio(
  chave: ChaveDeContagem,
  resumo: ResumoDosCadastros | undefined,
): { total: number; ativos: number } | null {
  if (ehCadastroDaApi(chave)) return resumo?.dominios[chave] ?? null;
  return { total: totalDoDominio(chave), ativos: totalAtivoDoDominio(chave) };
}

/**
 * Quais domínios cada hub reúne, e a frase que o descreve.
 *
 * Só o que NÃO cabe no mapa de navegação mora aqui: o mapa guarda rota, título
 * e ícone — que viram menu —, e este arquivo guarda a ligação com o dado, que é
 * assunto de tela. Manter os dois separados é o que permite trocar a origem do
 * dado sem tocar na navegação.
 */
export interface DescricaoDoHub {
  readonly resumo: string;
  /** Domínios cujo total soma no contador do cartão. */
  readonly dominios: readonly ChaveDeContagem[];
}

export const HUBS: Readonly<Record<string, DescricaoDoHub>> = {
  '/admin/parametros/cadastros': {
    resumo: 'Tabelas-base de referência usadas em formulários e validações do sistema.',
    dominios: [
      'marcas-veiculo',
      'modelos-veiculo',
      'versoes-veiculo',
      'tipos-veiculo',
      'cores-veiculo',
      'tipos-unidade-escolar',
      'categorias-cnh',
      'tipos-vinculo',
      'cursos-obrigatorios',
      'tipos-ocorrencia',
      'motivos-cancelamento-os',
      'motivos-substituicao',
      'tipos-publicacao',
    ],
  },
  '/admin/parametros/referencia': {
    resumo:
      'Entidades-base que outras telas do sistema referenciam (selects, validações, vínculos cruzados).',
    dominios: ['escolas', 'seguradoras'],
  },
  '/admin/parametros/documentos': {
    resumo: 'Requisitos documentais, cursos exigidos por papel e apólices por tipo de veículo.',
    dominios: ['requisitosDocumentais', 'cursos-obrigatorios', 'apolices'],
  },
  '/admin/parametros/financeiro': {
    resumo:
      'Tabela de Preços Contratuais (remuneração, retenções e IR), bases de cálculo de multa, glosas e capacidade.',
    dominios: [
      'basesCalculoMulta',
      'tiposGlosa',
      'capacidadeVeiculo',
      'documentosPagamento',
      'validacaoDocumentos',
    ],
  },
};
